import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem('sk_token') || null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(getStoredToken);
  const [planLimits, setPlanLimits] = useState(null);
  const [effectivePlan, setEffectivePlan] = useState(null);
  const [basePlan, setBasePlan] = useState(null);
  const [planOrigin, setPlanOrigin] = useState(null);
  const [powerUserExpiresAt, setPowerUserExpiresAt] = useState(null);
  const [planReady, setPlanReady] = useState(false);

  const setToken = useCallback((t) => {
    if (t) localStorage.setItem('sk_token', t);
    else localStorage.removeItem('sk_token');
    setTokenState(t);
  }, []);

  const getLanguageHeader = useCallback(() => {
    const raw =
      localStorage.getItem('sketchario_lang') ||
      localStorage.getItem('i18nextLng') ||
      document?.documentElement?.lang ||
      navigator?.language ||
      'it';
    return String(raw).toLowerCase().split('-', 1)[0] || 'it';
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    instance.interceptors.request.use((config) => {
      const t = localStorage.getItem('sk_token');
      if (t) config.headers.Authorization = `Bearer ${t}`;
      config.headers['X-Sketchario-Language'] = getLanguageHeader();
      return config;
    });
    instance.interceptors.response.use(
      (res) => res,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          try {
            const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            if (data.access_token) {
              localStorage.setItem('sk_token', data.access_token);
              setTokenState(data.access_token);
              error.config.headers.Authorization = `Bearer ${data.access_token}`;
              return instance(error.config);
            }
          } catch {
            setUser(null);
            setToken(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [getLanguageHeader, setToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const socialToken = params.get('social_token');
    const socialError = params.get('social_error');
    if (socialToken) {
      setToken(socialToken);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (socialError) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [setToken]);

  useEffect(() => {
    const t = localStorage.getItem('sk_token');
    if (!t) { setLoading(false); return; }
    api.get('/auth/me')
      .then(({ data }) => setUser(data.user))
      .catch(() => { setUser(null); setToken(null); })
      .finally(() => setLoading(false));
  }, [api, setToken]);

  const refreshPlanLimits = useCallback(async () => {
    const t = localStorage.getItem('sk_token');
    if (!t) {
      setPlanLimits(null);
      return null;
    }
    try {
      const { data } = await api.get('/plan/limits');
      setPlanLimits(data);
      return data;
    } catch {
      setPlanLimits(null);
      return null;
    }
  }, [api]);

  const refreshEffectivePlan = useCallback(async () => {
    const t = localStorage.getItem('sk_token');
    if (!t) {
      setEffectivePlan(null);
      setBasePlan(null);
      setPlanOrigin(null);
      setPowerUserExpiresAt(null);
      setPlanReady(true);
      return null;
    }
    try {
      const { data } = await api.get('/plan/entitlements');
      setEffectivePlan(data.effective_plan);
      setBasePlan(data.base_plan);
      setPlanOrigin(data.origin);
      setPowerUserExpiresAt(data.power_user_expires_at || null);
      return data;
    } catch {
      setEffectivePlan(null);
      setBasePlan(null);
      setPlanOrigin(null);
      setPowerUserExpiresAt(null);
      return null;
    } finally {
      setPlanReady(true);
    }
  }, [api]);

  useEffect(() => {
    if (!loading) {
      refreshPlanLimits();
      refreshEffectivePlan();
    }
  }, [loading, refreshPlanLimits, refreshEffectivePlan, token]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.access_token) setToken(data.access_token);
    setUser(data.user);
    setPlanReady(false);
    setTimeout(() => { refreshPlanLimits(); refreshEffectivePlan(); }, 0);
    return data;
  }, [api, refreshPlanLimits, refreshEffectivePlan, setToken]);

  const applyAuthSession = useCallback((data) => {
    if (data?.access_token) setToken(data.access_token);
    setUser(data?.user || null);
    setPlanReady(false);
    setTimeout(() => { refreshPlanLimits(); refreshEffectivePlan(); }, 0);
  }, [refreshPlanLimits, refreshEffectivePlan, setToken]);

  const register = useCallback(async (name, email, password) => {
    const language = getLanguageHeader();
    const { data } = await api.post('/auth/register', { name, email, password, language });
    return data;
  }, [api, getLanguageHeader]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(null);
    setPlanLimits(null);
    setEffectivePlan(null);
    setBasePlan(null);
    setPlanOrigin(null);
    setPowerUserExpiresAt(null);
    setPlanReady(false);
    setToken(null);
  }, [api, setToken]);

  const updateProfile = useCallback(async (updates) => {
    const { data } = await api.put('/profile', updates);
    setUser(data);
    return data;
  }, [api]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, updateProfile, api, token,
      planLimits, refreshPlanLimits, applyAuthSession,
      effectivePlan, basePlan, planOrigin, powerUserExpiresAt, planReady, refreshEffectivePlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext) || {};
